import React, { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { getDocuments, updateDocument } from '../../services/firebase';

type Role = 'salesman' | 'warehouse';

interface AppUser {
  documentId?: string; // Firestore document id
  id: string;          // logical id (same as document id in our app)
  name?: string;
  email?: string;
  photoURL?: string;
  warehouseId?: string;
  role?: Role;
}

const roleOptions: Role[] = ['salesman', 'warehouse'];

const PeopleSection: React.FC = () => {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const docs = await getDocuments('users');
      setUsers(docs as AppUser[]);
    } catch (e: any) {
      console.error('Failed to load users', e);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const onChangeRole = async (u: AppUser, newRole: Role) => {
    const docId = u.documentId || u.id;
    if (!docId) return;
    try {
      setSavingRole(prev => ({ ...prev, [docId]: true }));
      await updateDocument('users', docId, { role: newRole });
      setUsers(prev => prev.map(x => (x.id === u.id ? { ...x, role: newRole } : x)));
    } catch (e) {
      console.error('Failed to update role', e);
      alert('Failed to update role. Please try again.');
    } finally {
      setSavingRole(prev => ({ ...prev, [docId]: false }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="w-6 h-6 text-gray-700" />
          <h1 className="text-xl font-semibold text-gray-800">People</h1>
        </div>
        <button
          onClick={loadUsers}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Warehouse</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">Loading users…</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">No users found</td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <img
                          src={u.photoURL || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(u.name || u.email || u.id)}
                          alt={u.name || u.email || u.id}
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e: any) => {
                            e.currentTarget.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(u.name || u.email || u.id);
                          }}
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-800">{u.name || '-'}{u.id ? <span className="ml-2 text-xs text-gray-400">#{u.id}</span> : null}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{u.email || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{u.warehouseId || '-'}</td>
                    <td className="px-4 py-3">
                      <select
                        className="text-sm border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                        value={(u.role as Role) || 'salesman'}
                        onChange={(e) => onChangeRole(u, e.target.value as Role)}
                        disabled={!!savingRole[u.documentId || u.id]}
                        title="Only role is editable"
                      >
                        {roleOptions.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                      {savingRole[u.documentId || u.id] && (
                        <span className="ml-2 text-xs text-gray-500">Saving…</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PeopleSection;
